'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Loader2, ArrowRight, BadgeCheck } from 'lucide-react';
import { SILKAI } from '@/lib/silkai';
import {
  fmtCompactUsd,
  looksLikeMint,
  type TokenSuggestion,
} from '@/lib/web3/token-search';

const QUICK_PICKS = [
  { label: 'JUP',    mint: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN' },
  { label: 'BONK',   mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263' },
  { label: 'USDC',   mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' },
  { label: 'WIF',    mint: 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm' },
  { label: 'RAY',    mint: '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLYQbkX2' },
  { label: 'SILKAI', mint: SILKAI.mint },
] as const;

interface Props {
  loading: boolean;
  error: string;
  onCheck: (mint: string) => void;
}

export default function TokenSearchField({ loading, error, onCheck }: Props) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<TokenSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, TokenSuggestion[]>>(new Map());
  /**
   * Set to true after user picks from the suggestion list.
   * Prevents suggestions from re-opening when the input regains focus
   * or when the query effect re-fires with the chosen symbol still in the box.
   */
  const pickedRef = useRef(false);

  const pick = useCallback(
    (mint: string, symbol?: string) => {
      pickedRef.current = true;
      setQuery(symbol ?? mint);
      setOpen(false);
      setSuggestions([]);
      setActiveIdx(-1);
      onCheck(mint);
    },
    [onCheck],
  );

  useEffect(() => {
    const q = query.trim();

    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    // Don't re-search when the value was just set by pick()
    if (pickedRef.current) return;

    if (q.length < 3 || looksLikeMint(q)) {
      setSuggestions([]);
      setSearching(false);
      setOpen(false);
      return;
    }

    const cached = cacheRef.current.get(q);
    if (cached) {
      setSuggestions(cached);
      setOpen(cached.length > 0);
      setActiveIdx(-1);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setSearching(true);

      void fetch(`/api/token-search?q=${encodeURIComponent(q)}`, { signal: ctrl.signal })
        .then(async (res) => {
          if (!res.ok) return { results: [] as TokenSuggestion[] };
          return (await res.json()) as { results?: TokenSuggestion[] };
        })
        .then((data) => {
          if (ctrl.signal.aborted) return;
          const results = data.results ?? [];
          cacheRef.current.set(q, results);
          setSuggestions(results);
          setOpen(results.length > 0);
          setActiveIdx(-1);
        })
        .catch(() => {
          if (ctrl.signal.aborted) return;
          setSuggestions([]);
          setOpen(false);
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setSearching(false);
        });
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [query]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      const s = suggestions[activeIdx];
      if (s) pick(s.mint, s.symbol);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="w-full">
      <div ref={rootRef} className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            {(searching || loading) && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 animate-spin pointer-events-none" />
            )}
            <input
              type="text"
              value={query}
              onChange={(e) => {
                pickedRef.current = false;
                setQuery(e.target.value);
                if (e.target.value.trim().length >= 3 && !looksLikeMint(e.target.value.trim())) {
                  setOpen(true);
                }
              }}
              onFocus={() => {
                if (!pickedRef.current && suggestions.length > 0) setOpen(true);
              }}
              onKeyDown={onKeyDown}
              placeholder="Symbol (e.g. BONK) or paste mint address…"
              className="w-full h-13 pl-11 pr-11 bg-black/60 border border-white/12 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-rose-500/60 transition-colors h-12"
              aria-label="Token symbol or mint address"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls="token-suggestions"
              autoComplete="off"
              spellCheck={false}
            />

            {open && suggestions.length > 0 && (
              <ul
                id="token-suggestions"
                role="listbox"
                className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto custom-scrollbar border border-white/15 bg-neutral-950 shadow-2xl shadow-black/80"
              >
                {suggestions.map((s, i) => {
                  const sub = [
                    fmtCompactUsd(s.mcap) ? `${fmtCompactUsd(s.mcap)} mcap` : null,
                    s.holderCount != null ? `${s.holderCount.toLocaleString()} holders` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');

                  return (
                    <li key={s.mint} role="option" aria-selected={i === activeIdx}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(s.mint, s.symbol)}
                        className={`flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                          i === activeIdx ? 'bg-rose-500/10' : 'hover:bg-white/5'
                        }`}
                      >
                        {s.icon ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={s.icon}
                            alt=""
                            width={32}
                            height={32}
                            className="h-8 w-8 shrink-0 rounded-full bg-neutral-800 object-cover"
                          />
                        ) : (
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs font-semibold text-neutral-400">
                            {s.symbol.slice(0, 2)}
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="font-semibold text-white">{s.symbol}</span>
                            {s.verified && (
                              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                            )}
                          </span>
                          <span className="block truncate text-xs text-neutral-500">{s.name}</span>
                          {sub && <span className="block text-[10px] text-neutral-600">{sub}</span>}
                        </span>
                        <span className="shrink-0 font-mono text-[10px] text-neutral-600">
                          {s.mint.slice(0, 4)}…{s.mint.slice(-4)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <button
            onClick={() => {
              if (activeIdx >= 0 && suggestions[activeIdx]) {
                pick(suggestions[activeIdx].mint, suggestions[activeIdx].symbol);
                return;
              }
              const q = query.trim();
              if (looksLikeMint(q)) { onCheck(q); return; }
              if (suggestions.length === 1) { pick(suggestions[0].mint, suggestions[0].symbol); return; }
              onCheck(q);
            }}
            type="button"
            disabled={loading}
            className="inline-flex items-center justify-center h-12 px-6 bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors gap-2 disabled:opacity-60 shrink-0"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>Check <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-neutral-600">Try:</span>
          {QUICK_PICKS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => pick(p.mint, p.label)}
              className="text-[11px] font-mono text-neutral-500 border border-white/10 px-2 py-0.5 hover:border-rose-500/40 hover:text-white transition-colors cursor-pointer"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
    </div>
  );
}
